import { useState, useMemo, useCallback, useEffect } from 'react';
import type { Participant, OverallHealthStatus, TimeSlotResponse } from '../../../../types';
import type { GroupWithMembers } from '../../../../services/api/groups';
import { sessionsApi, type SessionWithDetails } from '../../../../services/api/sessions';
import { timeSlotsApi } from '../../../../services/api/timeSlots';
import {
  cohortCoachesApi,
  type CoachCalloutData,
} from '../../../../services/api/cohortCoaches';
import { useHealthSummary } from '../../hooks/useHealthSummary';

interface UseGroupsDataOptions {
  cohortId: string;
  groups: GroupWithMembers[];
  participants: Participant[];
}

export interface UseGroupsDataResult {
  /** Map of group ID -> sessions fetched for that group */
  groupSessionsMap: Record<string, SessionWithDetails[]>;
  /** Time slots defined for the cohort */
  cohortTimeSlots: TimeSlotResponse[];
  /** Coach callout banner data (EC-34, EC-36) */
  calloutData: CoachCalloutData | null;
  /** Re-fetch callout data (used after coach assignment changes) */
  fetchCalloutData: () => Promise<void>;
  /** Map of user_id -> Participant for quick lookup */
  participantMap: Record<string, Participant>;
  /** Map of group ID -> health status from the health summary */
  groupHealthMap: Record<string, OverallHealthStatus>;
}

/**
 * Fetches and maintains all auxiliary data required by the groups view:
 * sessions per group, cohort time slots, coach callout banners, participant
 * lookup map, and group health status.
 */
export function useGroupsData({
  cohortId,
  groups,
  participants,
}: UseGroupsDataOptions): UseGroupsDataResult {
  // ─── Sessions by group ──────────────────────────────────────────────
  const [groupSessionsMap, setGroupSessionsMap] = useState<Record<string, SessionWithDetails[]>>({});

  useEffect(() => {
    if (groups.length === 0) {
      setGroupSessionsMap({});
      return;
    }

    const fetchGroupSessions = async () => {
      const sessionsMap: Record<string, SessionWithDetails[]> = {};

      await Promise.all(
        groups.map(async (group) => {
          try {
            const sessions = await sessionsApi.listByGroup(cohortId, group.id);
            sessionsMap[group.id] = sessions;
          } catch (error) {
            console.error(`Failed to fetch sessions for group ${group.id}:`, error);
            sessionsMap[group.id] = [];
          }
        }),
      );

      setGroupSessionsMap(sessionsMap);
    };

    fetchGroupSessions();
  }, [cohortId, groups]);

  // ─── Cohort time slots ──────────────────────────────────────────────
  const [cohortTimeSlots, setCohortTimeSlots] = useState<TimeSlotResponse[]>([]);

  useEffect(() => {
    const fetchTimeSlots = async () => {
      try {
        const response = await timeSlotsApi.list(cohortId);
        setCohortTimeSlots(response.time_slots || []);
      } catch (error) {
        console.error('Failed to fetch time slots:', error);
        setCohortTimeSlots([]);
      }
    };
    fetchTimeSlots();
  }, [cohortId]);

  // ─── Coach callout data (EC-34, EC-36) ──────────────────────────────
  const [calloutData, setCalloutData] = useState<CoachCalloutData | null>(null);

  const fetchCalloutData = useCallback(async () => {
    try {
      const data = await cohortCoachesApi.getCalloutData(cohortId);
      setCalloutData(data);
    } catch (error) {
      console.error('Failed to fetch callout data:', error);
    }
  }, [cohortId]);

  useEffect(() => {
    if (groups.length > 0) {
      fetchCalloutData();
    }
  }, [groups.length, fetchCalloutData]);

  // ─── Participant lookup map ─────────────────────────────────────────
  const participantMap = useMemo(() => {
    const map: Record<string, Participant> = {};
    participants.forEach((p) => {
      map[p.user_id] = p;
    });
    return map;
  }, [participants]);

  // ─── Group health map ───────────────────────────────────────────────
  const { healthSummary } = useHealthSummary({ cohortId });

  const groupHealthMap = useMemo(() => {
    const map: Record<string, OverallHealthStatus> = {};

    // MED-394: Mark groups with 0 completed sessions as 'no_data'
    for (const group of groups) {
      const sessions = groupSessionsMap[group.id] || [];
      const hasCompletedSessions = sessions.some(s => s.status === 'completed');
      if (!hasCompletedSessions) {
        map[group.id] = 'no_data';
      } else {
        // Default to on_track for groups with completed sessions; alerts may override below
        map[group.id] = 'on_track';
      }
    }

    // Override with alert-derived status where applicable
    if (healthSummary) {
      const allAlerts = [
        ...healthSummary.tiers.risk.alerts,
        ...healthSummary.tiers.monitor.alerts,
      ];
      for (const alert of allAlerts) {
        if (alert.entityType === 'group' && alert.entityId) {
          if (alert.tier === 'risk') {
            map[alert.entityId] = 'at_risk';
          } else if (alert.tier === 'monitor' && map[alert.entityId] !== 'at_risk') {
            map[alert.entityId] = 'monitor';
          }
        }
      }
    }

    return map;
  }, [healthSummary, groups, groupSessionsMap]);

  return {
    groupSessionsMap,
    cohortTimeSlots,
    calloutData,
    fetchCalloutData,
    participantMap,
    groupHealthMap,
  };
}
